(() => {
  'use strict';
  const MAX_BYTES = 512 * 1024;
  const MAX_PUBLICATION_AGE_MS = 26 * 3600000;
  const money = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 2 });
  const labels = { available: '観測値あり', stale: '古いデータ', missing: '未取得', ambiguous: '対応確認中' };
  const sha = async (raw) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', raw)), (n) => n.toString(16).padStart(2, '0')).join('');
  const validDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

  function assetUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || ![window.location.origin, 'https://cdn.shopify.com'].includes(url.origin) || !/\/ts-ref-market-[a-f0-9]{64}\.json$/.test(url.pathname)) return null;
      return url.href;
    } catch { return null; }
  }
  async function boundedBytes(response, expected) {
    if (Number(response.headers.get('Content-Length')) > MAX_BYTES) throw new Error('size');
    if (!response.body?.getReader) {
      const raw = new Uint8Array(await response.arrayBuffer());
      if (raw.length !== expected || raw.length > MAX_BYTES) throw new Error('size');
      return raw;
    }
    const reader = response.body.getReader(), chunks = [];
    let size = 0;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > expected || size > MAX_BYTES) { await reader.cancel(); throw new Error('size'); }
        chunks.push(value);
      }
    } finally { reader.releaseLock(); }
    if (size !== expected) throw new Error('size');
    const raw = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) { raw.set(chunk, offset); offset += chunk.byteLength; }
    return raw;
  }
  function validPrice(point, id) {
    if (!point || point.reference_id !== id || point.currency !== 'JPY' || !Object.hasOwn(labels, point.status) || !validDay(point.snapshot_date) || point.sample_count !== null) return false;
    const priceKeys = ['price_min', 'price_median', 'price_max'];
    const countKeys = ['listing_count', 'in_stock_count'];
    if (priceKeys.some((key) => point[key] !== null && (!Number.isFinite(point[key]) || point[key] < 0)) || countKeys.some((key) => point[key] !== null && (!Number.isSafeInteger(point[key]) || point[key] < 0))) return false;
    if (point.status === 'ambiguous' && [...priceKeys, ...countKeys].some((key) => point[key] !== null)) return false;
    if (['available', 'stale'].includes(point.status) && (point.price_method !== 'source_approximate_median' || point.aggregate_status !== 'single_source_group' || priceKeys.some((key) => point[key] === null) || point.price_min > point.price_median || point.price_median > point.price_max || point.listing_count === null)) return false;
    return true;
  }
  async function validate(data, host) {
    const expected = host.dataset, now = Date.now();
    const expiry = Date.parse(data?.expires_at), pageExpiry = Date.parse(expected.expires);
    const snapshot = data?.snapshot, reference = data?.reference, series = data?.series;
    // Expiry limits current values, not the immutable, dated observations below.
    if (data?.schema_version !== 'market-ref-data/v1' || !Number.isFinite(expiry) || !Number.isFinite(pageExpiry) || expiry < pageExpiry || snapshot?.snapshot_id !== expected.snapshotId || snapshot.schema_version !== 1 || snapshot.freshness !== 'fresh' || snapshot.source_completeness !== 'unverified' || !validDay(snapshot.as_of)) throw new Error('contract');
    const generated = Date.parse(data.generated_at), verified = Date.parse(data.links_verified_at), snapshotGenerated = Date.parse(snapshot.generated_at), asOf = Date.parse(`${snapshot.as_of}T00:00:00+09:00`);
    if (![generated, verified, snapshotGenerated, asOf, now].every(Number.isFinite) || generated > now + 60000 || verified > now + 60000 || snapshotGenerated > now + 60000 || asOf > now + 60000 || expiry <= generated || expiry > Math.min(generated + MAX_PUBLICATION_AGE_MS, verified + MAX_PUBLICATION_AGE_MS, snapshotGenerated + 172800000, asOf + 172800000)) throw new Error('time');
    const currentPrice = data.market_price;
    if (currentPrice?.status === 'available' && (!validDay(currentPrice.snapshot_date) || currentPrice.snapshot_date > snapshot.as_of || expiry > Date.parse(`${currentPrice.snapshot_date}T00:00:00+09:00`) + 172800000)) throw new Error('time');
    if (reference?.canonical_url !== expected.canonicalUrl || !/^\/collections\/ref-[a-z0-9][a-z0-9-]{0,250}$/.test(reference.canonical_url) || reference.is_page_available !== true || reference.page_type !== 'ref' || reference.search_index_state !== 'index' || typeof reference.reference_id !== 'string' || !reference.reference_id || reference.reference_id.length > 2000 || await sha(new TextEncoder().encode(reference.reference_id)) !== expected.refKey) throw new Error('identity');
    if (series?.reference_id !== reference.reference_id || series.currency !== 'JPY' || series.gaps !== 'omitted' || !validDay(series.start) || !validDay(series.end) || series.end !== snapshot.as_of || series.start > series.end || Date.parse(series.end) - Date.parse(series.start) > 89 * 86400000 || !Array.isArray(series.items) || series.items.length > 90) throw new Error('series');
    let prior = '';
    for (const point of series.items) {
      if (!validPrice(point, reference.reference_id) || point.snapshot_date <= prior || point.snapshot_date < series.start || point.snapshot_date > series.end) throw new Error('point');
      prior = point.snapshot_date;
    }
    return series;
  }
  function cell(tag, value) {
    const node = document.createElement(tag);
    node.textContent = value;
    return node;
  }
  function renderSeries(series, host) {
    const table = document.createElement('table');
    table.append(cell('caption', `${series.start}〜${series.end}の観測値`));
    const thead = document.createElement('thead'), head = document.createElement('tr');
    for (const label of ['観測日', '近似中央値', '掲載数', '状態']) { const th = cell('th', label); th.setAttribute('scope', 'col'); head.append(th); }
    thead.append(head); table.append(thead);
    const body = document.createElement('tbody');
    for (const point of [...series.items].reverse()) {
      const row = document.createElement('tr'), date = cell('th', point.snapshot_date);
      date.setAttribute('scope', 'row'); row.append(date);
      const available = point.status === 'available';
      row.append(cell('td', available ? money.format(point.price_median) : '—'));
      row.append(cell('td', available ? `${point.listing_count}件` : '—'));
      row.append(cell('td', labels[point.status])); body.append(row);
    }
    table.append(body);
    host.querySelector('[data-history-content]').replaceChildren(table);
  }
  function setup(host) {
    if (host.dataset.marketInitialized) return;
    host.dataset.marketInitialized = 'true';
    const history = host.querySelector('[data-market-history]'), status = host.querySelector('[data-history-status]');
    if (!history) return;
    const serverHistory = history.querySelector('[data-history-ssr]');
    const expires = Date.parse(host.dataset.expires);
    let controller, timer, loading = false, loaded = false;
    const expire = () => {
      if (Number.isFinite(expires) && Date.now() < expires) return false;
      clearTimeout(timer);
      const values = host.querySelector('[data-market-values]');
      if (values) values.hidden = true;
      if (!Number.isFinite(expires)) { controller?.abort(); history.hidden = true; }
      host.querySelector('[data-market-status]').textContent = host.dataset.expiredMessage;
      return true;
    };
    const schedule = () => {
      if (expire()) return;
      clearTimeout(timer);
      timer = setTimeout(schedule, Math.min(expires - Date.now() + 1, 86400000));
    };
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('pageshow', schedule);
    schedule();
    // Generated graphs and observed rows are already in HTML; never replace them
    // with a second client-side history or require interaction for their content.
    if (serverHistory || !status) return;
    history.addEventListener('toggle', async () => {
      expire();
      if (!history.open || loading || loaded || !Number.isFinite(expires)) return;
      const url = assetUrl(host.dataset.assetUrl), expected = Number(host.dataset.assetBytes);
      if (!url || !Number.isSafeInteger(expected) || expected < 1 || expected > MAX_BYTES || !/^[a-f0-9]{64}$/.test(host.dataset.assetSha) || !globalThis.crypto?.subtle) { status.textContent = host.dataset.errorMessage; return; }
      loading = true;
      status.textContent = host.dataset.loadingMessage;
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(url, { credentials: 'omit', signal: controller.signal, redirect: 'error' });
        if (!response.ok || response.redirected || (response.url && assetUrl(response.url) !== url)) throw new Error('response');
        const raw = await boundedBytes(response, expected);
        if (await sha(raw) !== host.dataset.assetSha) throw new Error('integrity');
        const series = await validate(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(raw)), host);
        expire();
        if (series.items.length) { renderSeries(series, host); status.textContent = ''; }
        else status.textContent = host.dataset.emptyMessage;
        loaded = true;
      } catch {
        expire();
        status.textContent = host.dataset.errorMessage;
      } finally { clearTimeout(timeout); loading = false; }
    });
  }
  function init() { document.querySelectorAll('[data-ts-ref-market]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
